<template>
  <div v-if="isLoading" class="isLoadingDiv">
    <ProgressSpinner />
  </div>
  <div v-else-if="!isMaster && !schedule.finished">
    <h1 class="title">{{ $t("schedule-in-progress") }}</h1>
    <img class="centeredGif" :src="`${baseURL}/diamond.gif`" />
    <ProgressBar :value="progress" />
    <div class="p-mt-2" style="font-size: 1.5em">
      <div v-if="isRetry">{{ $t("retrying-schedule") }}</div>
      <div v-else>{{ $t("rotation-attempt-schedulestats-attempt", [scheduleStats.attempt]) }}</div>
      <div v-if="scheduleStats.attempt > 1 && scheduleStats.bestCount != scheduleStats.total">
        {{
          $t("best-result-so-far-schedulestats-bestcount-of-schedulestats-total-rotation-schedulestats-bestrotation", [
            scheduleStats.bestCount,
            scheduleStats.total,
            scheduleStats.bestRotation,
          ])
        }}
      </div>
      <div v-if="scheduleStats.scheduled > 0 || scheduleStats.bestCount > 0">
        {{
          $t("schedulestats-scheduled-scheduled-schedulestats-remaining-remaining", [
            scheduleStats.scheduled,
            scheduleStats.remaining,
          ])
        }}
      </div>
      <div v-else>{{ $t("waiting-to-start") }}</div>
    </div>
    <br />
    <div>
      <div class="p-mt-2">{{ $t("need-to-update-your-settings-schedule-taking-too-long") }}</div>
      <div class="p-mt-2" style="font-size: 1em; color: gray;">
        <span v-html="$t('complexScheduleNote', { supportLink: $t('supportLinkText') })"></span>
      </div>

      <br />
      <!-- cancel button - call schedules#cancel from api on click-->
      <Button
        class="p-button p-button-outlined p-button-danger"
        @click="cancelSchedule()"
        :disabled="isCanceling"
        :loading="isCanceling"
      >
        {{ isCanceling ? $t('canceling') : $t('cancel-scheduling-process') }}
      </Button>
    </div>
  </div>
  <div v-else-if="schedule" class="whiteBg">
    <UpgradeBanner v-if="currentUser && !currentUser.upgraded" :message="'Only showing some games on free trial.'" />
    <div v-if="!currentUser.upgraded"><br /><br /></div>
    <div v-if="!schedule.scheduled_items">
      <ProgressSpinner />
    </div>
    <div v-else-if="schedule.scheduled_items" class="p-text-left">
      <!-- <p class="title is-4" v-if="schedule.is_practice">
        ⭐️&nbsp;Practice Schedule
      </p> -->
      <div class="whiteBg" style="padding: 20px 0px">
        <h1
          class="title p-text-left is-2 is-1"
          v-bind:class="{
            'is-3': isMaster || (schedule.name && schedule.name.length > 20),
            'is-4': schedule.name && schedule.name.length > 40,
          }"
        >
          <span v-if="isMaster">{{ $t("master-game-list") }}</span>
          <span v-else-if="schedule.config && schedule.config.is_tournament">{{
            $t("tournament-schedule-name", [schedule.name])
          }}</span>
          <span v-else>{{ $t("schedule-schedule-name", [schedule.name]) }}</span>
          <div style="float: right" v-if="!isMaster">
            <Button class="p-button-sm p-button-secondary" @click="showActions = !showActions">
              <font-awesome-icon
                style="font-size: 2em"
                :icon="showActions ? ['fal', 'angle-up'] : ['fal', 'angle-down']"
              />
              {{ $t("schedule-actions") }}
            </Button>
          </div>
        </h1>
        <div v-if="showActions" class="p-my-3">
          <span v-if="!isMaster">
            <Button
              class="p-button p-button-outlined p-button-secondary mr-2"
              @click="retryModal = true"
              v-if="schedule && schedule.config && !schedule.config.is_tournament"
              :label="$t('retry-schedule')"
            />
            <Button
              class="p-button p-button-outlined p-button-secondary mr-2"
              @click="toggleLog"
              :label="$t('view-log')"
            />
            <Button
              class="p-button p-button-outlined p-button-secondary mr-2"
              @click="copyShareLink"
              :label="$t('share-link')"
            />
            <Button
              class="p-button p-button-outlined p-button-secondary mr-2"
              @click="copyEmbedCode"
              :label="$t('embed-code')"
            />
          </span>
          <Button
            class="p-button p-button-outlined p-button-secondary mr-2"
            @click="viewCalendar"
            :label="$t('view-calendar')"
          />
          <Button
            class="p-button p-button-outlined p-button-secondary mr-2"
            @click="editDialog = true"
            :label="$t('edit-name')"
          />
          <Button
            class="p-button p-button-outlined p-button-danger"
            @click="deleteDialog = true"
            :label="$t('Button.delete')"
          />
        </div>
        <div v-if="schedule && schedule.is_imported" style="font-size: 1.2em">
          <p>
            <Ball :color="schedule.division.color" />
            {{ $t("schedule-division-name-schedule-imported-or-manual", [schedule.division.name]) }}
          </p>
        </div>
        <div v-else-if="!isMaster" style="font-size: 1.2em">
          <p>
            <Ball :color="schedule.division.color" /> {{ schedule.division.name }} - {{ itemType }} -
            {{ scheduleSummary }}
          </p>
          <p v-if="scheduleSummary2"><Ball :color="schedule.division.color" /> {{ scheduleSummary2 }}</p>
          <p><Ball :color="schedule.division.color" /> {{ constraintsSummary || "No Constraints" }}</p>
          <p v-if="schedule.scheduled_items.length || schedule.unscheduled_items.length">
            <Ball :color="schedule.division.color" /> {{ $t("successrate-d-success", [successRate]) }} -
            <span class="is-success"
              >{{ $t("schedule-scheduled_items-length-scheduled", [schedule.scheduled_items.length]) }}
            </span>
            {{ $t("schedule-unscheduled_items-length-unscheduled", [schedule.unscheduled_items.length]) }}
          </p>
        </div>
      </div>

      <!-- <span>Minutes rest: {{ schedule.config.min_minutes_rest }}</span> &nbsp;
      <span>Days between games: {{ schedule.config.next_game_offset }}</span>
      &nbsp;
      <span>Max games / day: {{ schedule.config.max_games_day }}</span> &nbsp;
      <span>Max games / week: {{ schedule.config.max_games_week }}</span> &nbsp; -->
      <!-- <div class="p-my-4" v-if="!isMaster">
        &nbsp;
        <a
          v-if="seConnected && currentSeason.se_id && !schedule.is_practice"
          class="button"
          @click="exportSchedule(schedule)"
          >Sync to
          <img
            src="/se_logo_full.png"
            style="
              height: 1em;
              margin-top: 5px;
              vertical-align: middle;
              margin-left: 5px;
            "
        /></a>
      </div> -->
      <Dialog :header="$t('dialog.venueDayByDay')" :visible.sync="dbDialog" :style="{ width: '100vw' }" :modal="true">
        <div class="p-grid p-fluid" style="margin-top: 50px; padding: 10px;" id="dbTable">
          <div style="width: 100%; margin-bottom: 50px; text-align: center">
            <img
              v-if="isPrinting"
              style="height: 80px; margin: 0 auto; margin-bottom: 30px"
              src="https://diamond-public.s3.us-west-2.amazonaws.com/assets/diamond-logo-white.png"
            />
            <h1 style="text-align: center" class=" title is-1">
              {{ schedule.name || "Master Schedule" }} {{ $t("schedule.dayByDay") }}
            </h1>
            <br />
            <Button
              v-if="!isPrinting"
              style="width: 200px;"
              :label="$t('print')"
              class="p-button-lg"
              @click="dbPrint"
            />
          </div>
          <div v-for="(venue, name) in dbData" class="dbDiv" style="margin-bottom: 80px">
            <h1 class="title is-1" style="margin-bottom: 50px; text-align: center">
              {{ new Date(name).toDateString() }}
            </h1>
            <br />
            <table class="table" style="width: 100%">
              <tr>
                <th>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</th>
                <th v-for="v in getDbVenues(venue)" class="p-text-center">
                  <small
                    ><strong>{{ v }}</strong></small
                  >
                </th>
              </tr>
              <tr v-for="(entries, time) in venue">
                <td>{{ time }}</td>
                <td v-for="v in getDbVenues(venue)" class="p-text-center">{{ entries[v] || "" }}</td>
              </tr>
            </table>
          </div>
          <!-- {{dbData}} -->
        </div>
      </Dialog>

      <Dialog :header="$t('dialog.balances')" :visible.sync="balanceDialog" :style="{ width: '100vw' }" :modal="true">
        <div v-if="!schedule.balances" class="p-grid p-fluid">
          <div class="p-field p-col-12">
            <div class="p-text-center">
              <ProgressSpinner />
            </div>
          </div>
        </div>
        <div v-else class="p-grid p-fluid">
          <div class="p-field p-col-12">
            <div class="p-text-center">
              <Button
                style="width: 200px"
                :label="$t('print-all-balances')"
                class="p-button-lg"
                @click="printAllBalances"
              />
              &nbsp;&nbsp;
              <Button
                style="width: 200px"
                :label="$t('Button.done')"
                class="p-button-lg p-button-secondary"
                @click="balanceDialog = false"
              />
            </div>
            <div id="allBalances">
              <div v-if="isAll">
                <div class="text-align: center">
                  <a href="https://cactusware.com" target="_blank"
                    ><img
                      @click="printing = false"
                      style="width: 100%; margin: 0 auto; max-width: 200px"
                      src="https://diamond-public.s3.us-west-2.amazonaws.com/assets/diamond-logo-white.png"
                  /></a>
                  <br /><br />
                  <div v-if="isAll">
                    <h1
                      class="title is-1"
                      style="margin: 30px auto; text-align: center"
                      v-if="schedule && schedule.config && schedule.config.is_tournament"
                    >
                      {{ $t("tournament-schedule-name", [schedule.name]) }} <br />
                    </h1>
                    <h1 class="title is-1" style="margin: 30px auto; text-align: center" v-else>
                      {{ $t("schedule-schedule-name", [schedule.name]) }} <br />
                    </h1>
                  </div>
                </div>
              </div>
              <div v-if="schedule.balances && !schedule.is_practice" class="p-mt-3" id="teamBalance">
                <h3 class="title is-3" style="text-align: center; margin-top: 20px">
                  <span v-if="isPrinting && !isAll">
                    {{ $t("schedule-schedule-name", [schedule.name]) }} <br /><br
                  /></span>
                  {{ $t("team-balance") }}
                </h3>
                <div v-if="!isPrinting" class="p-text-center">
                  <button class="button p-my-2 p-mx-2" @click="printTable('teamBalance')">
                    {{ $t("Button.print-table") }}
                  </button>
                  <button class="button p-my-2 p-mx-2" @click="exportTable('teams')">
                    {{ $t("Button.download-csv") }}
                  </button>
                  <br />
                </div>
                <div>
                  <strong>{{ $t("format") }}:</strong>
                  {{ $t("total-matchups-home-away-left-column-is-home-top-is-away") }}
                </div>
                <br />
                <DataTable
                  :value="schedule.balances.team_balance"
                  id="teamsTable"
                  ref="teamsTable"
                  class="p-datatable-sm p-datatable-striped"
                >
                  <Column
                    v-for="(b, i) in schedule.balances.team_names"
                    :header="schedule.balances.team_names[i]"
                    :field="schedule.balances.team_names[i]"
                    :key="i"
                  ></Column>
                </DataTable>
              </div>

              <div v-if="schedule.balances" class="p-mt-3" id="venueBalance">
                <br />
                <h3 class="title is-3" style="text-align: center; margin-top: 20px">
                  <span v-if="isPrinting && !isAll">
                    {{ $t("schedule-schedule-name", [schedule.name]) }} <br /><br
                  /></span>
                  {{ $t("venue-balance") }}
                </h3>
                <div v-if="!isPrinting" class="p-text-center">
                  <button class="button p-my-2 p-mx-2" @click="printTable('venueBalance')">
                    {{ $t("Button.print-table") }}
                  </button>
                  <button class="button p-my-2 p-mx-2" @click="exportTable('venues')">
                    {{ $t("Button.download-csv") }}
                  </button>
                  <br />
                </div>
                <br />
                <DataTable
                  id="venuesTable"
                  ref="venuesTable"
                  :value="schedule.balances.venue_balance"
                  class="p-datatable-sm p-datatable-striped"
                >
                  <Column
                    v-for="(b, i) in schedule.balances.venue_names"
                    :header="schedule.balances.venue_names[i]"
                    :field="schedule.balances.venue_names[i]"
                    sortable
                    :key="i"
                  ></Column>
                </DataTable>
                <br />
              </div>

              <div v-if="schedule.balances" class="p-mt-3" id="travelBalance">
                <h3 class="title is-3" style="text-align: center; margin-top: 20px">
                  <span v-if="isPrinting && !isAll">
                    {{ $t("schedule-schedule-name", [schedule.name]) }} <br /><br
                  /></span>
                  <span v-if="!schedule.is_practice">{{ $t("home-away-and") }} </span> {{ $t("early-late-balance") }}
                </h3>
                <div v-if="!isPrinting" class="p-text-center">
                  <button class="button p-my-2 p-mx-2" @click.stop="printTable('travelBalance')">
                    {{ $t("Button.print-table") }}
                  </button>
                  <button class="button p-my-2 p-mx-2" @click.stop="exportTable('times')">
                    {{ $t("Button.download-csv") }}
                  </button>
                  <br />
                </div>
                <br />
                <DataTable
                  id="timesTable"
                  ref="timesTable"
                  :value="schedule.balances.travel_balance"
                  class="p-datatable-sm p-datatable-striped"
                >
                  <Column
                    v-for="(b, i) in schedule.balances.travel_names"
                    :header="schedule.balances.travel_names[i]"
                    :field="schedule.balances.travel_names[i]"
                    :key="i"
                  ></Column>
                </DataTable>
                <br />
              </div>

              <div v-if="schedule.balances" class="p-mt-3" id="teamWeekBalance">
                <div v-if="isPrinting">{{ schedule.name }}</div>
                <h3 class="title is-3" style="text-align: center; margin-top: 20px">
                  <span v-if="isPrinting && !isAll">
                    {{ $t("schedule-schedule-name", [schedule.name]) }} <br /><br
                  /></span>
                  <span v-if="!schedule.is_practice">{{ $t("team-week-balance") }}</span>
                </h3>
                <div v-if="!isPrinting" class="p-text-center">
                  <button class="button p-my-2 p-mx-2" @click="printTable('teamWeekBalance')">
                    {{ $t("Button.print-table") }}
                  </button>
                  <button class="button p-my-2 p-mx-2" @click="exportTable('weeks')">
                    {{ $t("Button.download-csv") }}
                  </button>
                  <br /><br />
                </div>
                <DataTable
                  id="weekTable"
                  ref="weekTable"
                  :value="schedule.balances.week_balance"
                  class="p-datatable-sm p-datatable-striped"
                >
                  <Column
                    v-for="(b, i) in schedule.balances.week_names"
                    :header="schedule.balances.week_names[i]"
                    :field="schedule.balances.week_names[i]"
                    :key="i"
                  ></Column>
                </DataTable>
                <br />
              </div>

              <div v-if="schedule.balances" class="p-mt-3" id="daysTable">
                <h3 class="title is-3" style="text-align: center; margin-top: 20px">
                  <span v-if="isPrinting && !isAll">
                    {{ $t("schedule-schedule-name", [schedule.name]) }} <br /><br
                  /></span>
                  {{ $t("days-of-week-balance") }}
                </h3>
                <div v-if="!isPrinting" class="p-text-center">
                  <button class="button p-my-2 p-mx-2" @click="printTable('daysTable')">
                    {{ $t("Button.print-table") }}
                  </button>
                  <button class="button p-my-2 p-mx-2" @click="exportTable('days')">
                    {{ $t("Button.download-csv") }}
                  </button>
                  <br />
                </div>
                <br />
                <DataTable
                  id="daysTable"
                  ref="daysTable"
                  :value="schedule.balances.day_balance"
                  class="p-datatable-sm p-datatable-striped"
                >
                  <Column
                    v-for="(b, i) in schedule.balances.day_names"
                    :header="schedule.balances.day_names[i]"
                    :field="schedule.balances.day_names[i]"
                    :key="i"
                  ></Column>
                </DataTable>
                <br />
              </div>
            </div>
            <br /><br />
            <div v-if="schedule.balances" class="p-mt-3">
              <h3 class="title is-3" style="text-align: center; margin-top: 20px">
                <span v-if="!schedule.is_practice">{{ $t("rest-teams-per-week") }}</span>
              </h3>
              <button class="button p-my-2 p-mx-2" @click="exportRest">{{ $t("Button.download-csv") }}</button>
              <br /><br />

              <table>
                <tr v-for="(b, i) in schedule.balances.week_names" :key="i">
                  <td>
                    {{ b == "Team" ? "" : b }}
                  </td>
                  <td class="p-text-left" style="font-weight: 400">
                    &nbsp; &nbsp; {{ (schedule.balances.cweek_balance[b] || []).join(", ") }}
                  </td>
                </tr>
              </table>

              <br />
              <br />
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog :header="$t('dialog.editSchedule')" :visible.sync="editDialog" :style="{ width: 'auto' }" :modal="true">
        <div class="p-grid p-fluid" style="max-width: 400px">
          <div class="p-field p-col-12">
            <InputText v-model="newName" :placeholder="$t('enter-name')"></InputText>
          </div>
          <div class="p-field p-col-12">
            <Button :label="$t('Button.save')" @click="save" />
            <br /><br />
            <Button :label="$t('Button.cancel')" @click="editDialog = false" class="p-button-small p-button-text" />
          </div>
        </div>
      </Dialog>

      <Dialog
        :header="$t('dialog.confirmDeletion')"
        :visible.sync="deleteDialog"
        :style="{ width: '800px', maxWidth: '100%' }"
        :modal="true"
      >
        <div class="p-grid p-fluid">
          <div class="p-field p-col-12">
            {{ $t("are-you-sure-this-will-delete-all-schedule-data-this-cannot-be-undone") }}
          </div>
          <div v-if="seConnected && currentSeason.se_id" class="p-field-checkbox">
            <Checkbox name="isumpire" :binary="true" v-model="deleteSe" />
            <label for="isumpire"> {{ $t("delete-schedule-games-synced-to-sportsengine-if-exist") }}</label>
          </div>
          <div class="p-field p-col-12">
            <div style="max-width: 200px; margin: 0 auto; text-align: center">
              <Button :label="$t('dont-delete')" @click="deleteDialog = false" />
              <br /><br />
              <Button
                v-if="!deleting"
                :label="$t('delete-schedule')"
                @click="startDelete"
                class="p-button-danger p-button-text"
              />
              <ProgressSpinner v-else />
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        :header="$t('dialog.retrySchedule')"
        :visible.sync="retryModal"
        :style="{ width: 'auto', maxWidth: '90vw' }"
        :modal="true"
      >
        <div class="p-grid p-fluid">
          <div class="p-field p-col-12 p-mt-5">
            <h3 class="title is-4 p-text-center">{{ $t("which-games-to-retry") }}</h3>
            <div class="p-text-center p-mt-5">
              <Button
                style="max-width: 350px; text-align: center"
                class="p-button-secondary p-button-lg"
                v-bind:class="{ 'p-button-outlined': !newConfig.fresh }"
                @click="newConfig.fresh = true"
                :label="
                  $t('all-schedule-is_practice-practices-games-totalgameslength', [
                    schedule.is_practice ? `Practices` : `Games`,
                    totalGamesLength,
                  ])
                "
              >
              </Button>
              &nbsp;
              <Button
                :disabled="!schedule.unscheduled_items.length"
                style="max-width: 350px; text-align: center"
                class="p-button-secondary p-button-lg"
                v-bind:class="{ 'p-button-outlined': newConfig.fresh }"
                @click="newConfig.fresh = false"
                :label="
                  $t('unscheduled-schedule-is_practice-practices-games-schedule-unscheduled_items-length', [
                    schedule.is_practice ? `Practices` : `Games`,
                    schedule.unscheduled_items.length,
                  ])
                "
              >
              </Button>
            </div>
          </div>
          <div class="p-col-12 p-mt-5">
            <h3 class="title is-4 p-text-center">{{ $t("edit-schedule-constraints") }}</h3>
          </div>
          <div class="p-col-4 p-my-3">
            <div style="max-width: 420px; margin: 0 auto; text-align: center">
              <div class="p-field-checkbox">
                <Checkbox v-model="newConfig.use_coach_conflict_method" :binary="true" />&nbsp;
                <label style="font-size: 1.4em"> {{ $t("avoid-double-booking-coaches") }} </label>
              </div>
            </div>
          </div>
          <div class="p-col-4 p-my-3">
            <div
              style="max-width: 420px; margin: 0 auto; text-align: center"
              v-tooltip="'Only allow the home teams assigned to a venue be scheduled as home'"
            >
              <div class="p-field-checkbox">
                <Checkbox v-model="newConfig.enforce_home" :binary="true" />&nbsp;
                <label style="font-size: 1.4em"> {{ $t("enforce-home-team-venues") }} </label>
              </div>
            </div>
          </div>
          <div class="p-col-4 p-my-3">
            <div style="max-width: 420px; margin: 0 auto; text-align: center">
              <div class="p-field-checkbox">
                <Checkbox v-model="newConfig.do_balance" :binary="true" />&nbsp;
                <label style="font-size: 1.4em"> {{ $t("balance-days-times-venues") }} </label>
                <br />
              </div>
              <small> {{ $t("may-result-in-unscheduled-games") }} </small>
            </div>
          </div>
          <div class="p-col-4 p-my-3">
            <div
              style="max-width: 420px; margin: 0 auto; text-align: center"
              v-tooltip="'Attempts to schedule teams for one game during the week and one game on the weekend'"
            >
              <div class="p-field-checkbox">
                <Checkbox v-model="newConfig.use_weekend_method" :binary="true" />&nbsp;
                <label style="font-size: 1.4em"> {{ $t("use-weekend-method") }} </label>
              </div>
              <div v-if="newConfig.use_weekend_method" class="p-field-checkbox">
                <Checkbox v-model="newConfig.friday_as_weekend" :binary="true" />&nbsp;
                <label> {{ $t("assume-friday-as-weekend") }} </label>
              </div>
              <div v-if="newConfig.use_weekend_method">
                <small>{{ $t("weekend-method-will-default-to-teams-playing-max-2-games-week") }}</small>
              </div>
            </div>
          </div>
          <div class="p-col-4 p-my-3"></div>
          <div class="p-col-4 p-my-3">
            <div style="max-width: 420px; margin: 0 auto; text-align: center">
              <div class="p-field-checkbox">
                <Checkbox
                  v-model="newConfig.balance_venues"
                  :binary="true"
                  @change="toggleVenueBalanceDialog(newConfig.balance_venues)"
                />&nbsp;
                <label style="font-size: 1.4em"
                  >{{ $t("use-venue-priority-balancing") }}
                  <font-awesome-icon
                    class="cursorpointer menuIcon"
                    style="color: #2196F3;"
                    v-show="newConfig.balance_venues"
                    @click="toggleVenueBalanceDialog(true)"
                    :icon="['fal', 'cog']"
                  />
                </label>
              </div>
            </div>
          </div>
          <div class="p-col-3">
            <div class="constraintBox">
              <label for="horizontal">{{
                $t("max-schedule-is_practice-practices-games-day", [schedule.is_practice ? "Practices" : "Games"])
              }}</label>
              <br />
              <a @click="newConfig.max_games_day = 1" v-show="newConfig.max_games_day == 0">
                {{ $t("Button.add-constraint") }}
              </a>
              <InputNumber
                v-show="newConfig.max_games_day > 0"
                class="configNumber p-mt-1"
                v-model="newConfig.max_games_day"
                showButtons
                buttonLayout="horizontal"
                :step="1"
                :min="0"
                :max="10"
                @input="configUpdate(d)"
                incrementButtonIcon="pi pi-plus"
                decrementButtonIcon="pi pi-minus"
              />
            </div>
          </div>
          <div class="p-col-3">
            <div class="constraintBox">
              <label for="horizontal">{{
                $t("max-schedule-is_practice-practices-games-week", [schedule.is_practice ? "Practices" : "Games"])
              }}</label>
              <br />
              <a
                v-if="newConfig.use_weekend_method != true"
                @click="newConfig.max_games_week = Math.max(1, newConfig.max_games_day)"
                v-show="newConfig.max_games_week == 0"
              >
                {{ $t("Button.add-constraint") }}
              </a>
              <i v-else> {{ $t("default-to-max-2") }} </i>
              <InputNumber
                v-show="newConfig.max_games_week > 0"
                class="configNumber p-mt-1"
                v-model="newConfig.max_games_week"
                showButtons
                buttonLayout="horizontal"
                :step="1"
                :min="0"
                :max="70"
                @input="configUpdate(d)"
                incrementButtonIcon="pi pi-plus"
                decrementButtonIcon="pi pi-minus"
              />
            </div>
          </div>
          <div class="p-col-3">
            <div class="constraintBox">
              <label
                for="horizontal"
                v-tooltip="
                  $t('minutesBetweenScheduleTooltipText', [schedule.is_practice ? $t('practices') : $t('games')])
                "
              >
                {{
                  $t("minutes-between-schedule-is_practice-practices-games", [
                    schedule.is_practice ? "Practices" : "Games",
                  ])
                }}
              </label>
              <br />
              <a
                v-if="newConfig.max_games_day != 1 && newConfig.max_games_day != 1"
                @click="newConfig.min_minutes_rest = 60"
                v-show="newConfig.min_minutes_rest == 0"
              >
                {{ $t("Button.add-constraint") }}
              </a>
              <i v-else> {{ $t("not-available") }} </i>
              <InputNumber
                v-show="newConfig.min_minutes_rest > 0"
                :disabled="newConfig.next_game_offset != 0 || newConfig.max_games_day <= 1"
                class="configNumber p-mt-1"
                v-model="newConfig.min_minutes_rest"
                showButtons
                buttonLayout="horizontal"
                :step="15"
                :min="0"
                :max="1440"
                @input="configUpdate(d)"
                incrementButtonIcon="pi pi-plus"
                decrementButtonIcon="pi pi-minus"
              />
            </div>
          </div>
          <div class="p-col-3">
            <div class="constraintBox">
              <label
                for="horizontal"
                v-tooltip="
                  $t('nightsBetweenScheduleTooltipText', [schedule.is_practice ? $t('practices') : $t('games')])
                "
              >
                {{
                  $t("nights-between-schedule-is_practice-practices-games", [
                    schedule.is_practice ? "Practices" : "Games",
                  ])
                }}
              </label>
              <br />
              <a
                v-if="newConfig.max_games_week != 1 && newConfig.max_games_day < 2"
                v-show="newConfig.next_game_offset == 0"
                @click="newConfig.next_game_offset = 1"
              >
                {{ $t("Button.add-constraint") }}
              </a>
              <i v-else> {{ $t("not-available") }}</i>
              <InputNumber
                v-show="newConfig.next_game_offset > 0"
                class="configNumber p-mt-1"
                :disabled="newConfig.max_games_day != 1"
                v-model="newConfig.next_game_offset"
                showButtons
                buttonLayout="horizontal"
                :step="1"
                :min="0"
                :max="30"
                @input="configUpdate(d)"
                incrementButtonIcon="pi pi-plus"
                decrementButtonIcon="pi pi-minus"
              />
            </div>
          </div>
          <div style="width: 300px; margin: 0px auto">
            <br /><br /><br />
            <Button :label="$t('submit-retry')" class="p-button-lg" @click="submitRetry" />
            <br /><br />
            <Button :label="$t('Button.cancel')" @click="retryModal = false" class="p-button-text p-button-lg" />
          </div>
        </div>
        <!-- <template #footer>
          <Button
            :label="$t('Button.cancel')"
            icon="pi pi-times"
            @click="retryModal = false"
            class="p-button-text"
          />
          <Button label="Submit" @click="submitRetry" icon="pi pi-check" />
        </template> -->
      </Dialog>

      <Dialog
        :header="$t('venue-configuration')"
        :visible.sync="venueBalanceEditDialog"
        :style="{ width: 'auto' }"
        :modal="true"
      >
        <div class="p-grid p-fluid" style="max-width: 400px">
          <div class="p-field p-col-12">
            <div class="p-mb-3" style="font-size: 1.5em; line-height: 2em; float: right; width: 200px;">
              <font-awesome-icon
                class="cursorpointer menuIcon"
                style="color: #2196F3;"
                @click="showVenueBalanceWindowInfo = true"
                :icon="['fal', 'info-circle']"
              />&nbsp;
              <span></span>
            </div>
            <b-radio v-model="newConfig.venue_balance_method" native-value="best_first">{{
              $t("use-best-venues-first")
            }}</b-radio>
            <b-radio v-model="newConfig.venue_balance_method" native-value="evenly">{{
              $t("evenly-distribute-venues")
            }}</b-radio>
            <div class="p-field p-col-12">
              <div class="row">
                <b-radio :disabled="!isSelectedEvenly" v-model="newConfig.venue_balance_tolerance" native-value="low">{{
                  $t("low")
                }}</b-radio>
              </div>
              <div class="row">
                <b-radio
                  :disabled="!isSelectedEvenly"
                  v-model="newConfig.venue_balance_tolerance"
                  native-value="medium"
                  >{{ $t("medium") }}</b-radio
                >
              </div>
              <div class="row">
                <b-radio
                  :disabled="!isSelectedEvenly"
                  v-model="newConfig.venue_balance_tolerance"
                  native-value="high"
                  >{{ $t("high") }}</b-radio
                >
              </div>
            </div>
          </div>
          <div class="p-field p-col-12">
            <Button :label="$t('Button.save')" @click="venueBalanceEditDialog = false" />
            <br /><br />
            <Button
              :label="$t('Button.cancel')"
              @click="venueBalanceEditDialog = false"
              class="p-button-small p-button-text"
            />
          </div>
        </div>
      </Dialog>

      <Dialog :visible.sync="showVenueBalanceWindowInfo" :modal="true" :style="{ width: 'auto' }" :show-header="false">
        <div class="p-grid p-fluid" style="max-width: 400px">
          <div class="p-field p-col-12" style="min-height: 40px">
            <div class="p-mt-5" style="font-size: 1.5em; line-height: 2em; float: right; width: 200px;">
              <span style="font-size: 1.2em; font-weight: bold">
                <font-awesome-icon
                  class="cursorpointer menuIcon"
                  style="color: #2196F3;"
                  :icon="['fal', 'info-circle']"
                />&nbsp;
                <span></span>
              </span>
            </div>
          </div>
          <div class="p-field p-col-12">
            <h3 class="title is-5">
              {{ $t("use-best-first") }}
            </h3>
            <p>
              {{
                $t(
                  "attempts-to-use-timeslots-in-venues-with-higher-priorities-3-is-highest-first-please-update-venue-priority-score-on-the-individual-venue-page",
                )
              }}
            </p>
            <br />
            Example:
            <table id="balanceExampleTable">
              <tr>
                <th>{{ $t("GameTable.column.team") }}&nbsp;</th>
                <th>{{ $t("venue-x-3") }}</th>
                <th>{{ $t("venue-y") }}</th>
                <th>{{ $t("venue-z-3") }}</th>
              </tr>
              <tr style="height: 2em; text-align: right;">
                <th style="text-align: center;">{{ $t("team-1b") }}&nbsp;</th>
                <td>11</td>
                <td>4</td>
                <td>0</td>
              </tr>
              <tr style="height: 2em; text-align: right;">
                <th style="text-align: center;">{{ $t("team-2b") }}&nbsp;</th>
                <td>10</td>
                <td>5</td>
                <td>0</td>
              </tr>
              <tr style="height: 2em; text-align: right;">
                <th style="text-align: center;">{{ $t("team-3b") }}</th>
                <td>11</td>
                <td>4</td>
                <td>0</td>
              </tr>
              <tr style="height: 2em; text-align: right;">
                <th style="text-align: center;">{{ $t("team-4b") }}</th>
                <td>10</td>
                <td>5</td>
                <td>0</td>
              </tr>
            </table>
          </div>
          <div class="p-field p-col-12">
            <h3 class="title is-5 p-my-3">
              {{ $t("evenly-distribute") }}
            </h3>
            <p>{{ $t("ignores-priority-score-and-attempts-to-even-out-the-distribution-of-venues-across-teams") }}</p>
            <br />
            Example:
            <table id="balanceExampleTable">
              <tr>
                <th>{{ $t("GameTable.column.team") }}&nbsp;</th>
                <th>{{ $t("venue-x-3") }}</th>
                <th>{{ $t("venue-y-0") }}</th>
                <th>{{ $t("venue-z-3") }}</th>
              </tr>
              <tr style="height: 2em; text-align: right;">
                <th style="text-align: center;">{{ $t("team-1b") }}&nbsp;</th>
                <td>5</td>
                <td>5</td>
                <td>5</td>
              </tr>
              <tr style="height: 2em; text-align: right;">
                <th style="text-align: center;">{{ $t("team-2b") }}&nbsp;</th>
                <td>5</td>
                <td>5</td>
                <td>5</td>
              </tr>
              <tr style="height: 2em; text-align: right;">
                <th style="text-align: center;">{{ $t("team-3b") }}</th>
                <td>5</td>
                <td>5</td>
                <td>5</td>
              </tr>
              <tr style="height: 2em; text-align: right;">
                <th style="text-align: center;">{{ $t("team-4b") }}</th>
                <td>5</td>
                <td>5</td>
                <td>5</td>
              </tr>
            </table>
          </div>
          <div class="p-field p-col-12">
            <br />
            <br />
            <Button
              :label="$t('Button.close')"
              @click="showVenueBalanceWindowInfo = false"
              class="p-button-small p-button-text"
            />
          </div>
        </div>
      </Dialog>

      <Reschedule
        v-if="schedule.unscheduled_items.length > 0"
        :schedule="schedule"
        v-on:assigned="assign"
        v-on:deleted="rescheduleDelete"
        v-on:refresh="getSchedule"
        v-on:getBalanceTables="doGetBalance(false)"
      />
      <div v-if="schedule.unscheduled_items.length > 0">
        <br />
        <div class="whiteBg" style="margin-bottom: -15px">
          <h1 class="title is-2 p-p-5" style="padding: 5px !important;">
            {{
              $t("scheduled-schedule-is_practice-practices-games-schedule-scheduled", [
                schedule.is_practice ? "Practices" : "Games",
                schedule.scheduled,
              ])
            }}
          </h1>
        </div>
      </div>
      <GameTable
        :schedule="schedule"
        v-on:dobalances="doGetBalance"
        v-on:refresh="getSchedule"
        :refreshSchedule="getSchedule"
        v-on:dbd="startDayByDay"
        v-on:openslots="getOpenSlots"
        :master="isMaster"
        v-on:update="updateItems"
        v-on:gamecreated="gameCreated"
        v-on:gameupdated="gameUpdated"
      ></GameTable>
      <br /><br />
      <div v-if="loadingBalances" class="p-text-center">
        <ProgressSpinner />
      </div>
      <div v-else-if="!schedule.balances && schedule && schedule.scheduled_items.length" class="p-text-center">
        <h2 class="title is-3">{{ $t("venue-team-time-balances") }}</h2>
        <br />
        <Button :label="$t('show-balances')" class="p-button-lg" @click="doGetBalance" />
        <br /><br /><br />
      </div>
      <div v-else-if="schedule && schedule.scheduled_items.length" class="p-text-center">
        <br />
        <Button :label="$t('show-balances')" class="p-button-lg" @click="doGetBalance" />
        &nbsp;&nbsp;&nbsp;
        <Button :label="$t('print-all-balances')" class="p-button-lg" @click="printAllBalances" />
        <br /><br />
      </div>
    </div>

    <!-- <div v-if="schedule.balances">
      <br />Scoring Balance
      <br />
      <button class="button" @click="printTable('scoresTable')">
        Print Table
      </button>
      <button class="button" @click="exportTable('scores')">
        Download CSV
      </button>
      <DataTable
        sortField="Wins"
        :sortOrder="-1"
        id="scoresTable"
        ref="scoresTable"
        :value="schedule.balances.score_balance"
        class="p-datatable-sm p-datatable-striped"
      >
        <Column
          v-for="(b, i) in schedule.balances.score_names"
          :header="schedule.balances.score_names[i]"
          :field="schedule.balances.score_names[i]"
          :key="i"
          sortable
        ></Column>
      </DataTable>
      <br />
    </div> -->
  </div>
</template>

<script>
import api from "@/api";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import GameTable from "@/components/GameTable";
import ColumnGroup from "primevue/columngroup"; //optional for column grouping

export default {
  components: { DataTable, Column, GameTable },
  // dddd
  data() {
    return {
      dbDialog: false,
      dbData: null,
      dbVenues: [],
      isRetry: false,
      isCanceling: false,
      scheduleStats: {
        attempt: 1,
        scheduled: 0,
        remaining: 0,
        total: 0,
        percentage: 0,
        bestCount: 0,
        bestRotation: 1,
      },
      balanceDialog: false,
      isAll: false,
      isPrinting: false,
      showingOpenSlots: false,
      deleting: false,
      showActions: true,
      deleteSe: false,
      deleteDialog: false,
      newName: "",
      editDialog: false,
      filterChoice: "team",
      selectedTeams: [],
      selectedVenues: [],
      selectedDivisions: [],
      checkInterval: null,
      progress: 1,
      schedule: null,
      isLoading: true,
      loadingBalances: false,
      retryModal: false,
      filters: {},
      newConfig: {
        fresh: false,
        min_minutes_rest: 0,
        next_game_offset: 0,
        max_games_day: 0,
        max_games_week: 0,
        use_coach_conflict_method: true,
        do_balance: true,
        enforce_home: true,
        use_weekend_method: false,
        friday_as_weekend: false,
        balance_venues: false,
        venue_balance_method: "best_first",
        venue_balance_tolerance: "low",
        venueBalanceEditDialog: false,
      },
      columns: null,
      selectedColumns: null,
      venueBalanceEditDialog: false,
      showVenueBalanceWindowInfo: false,
    };
  },
  computed: {
    successRate() {
      return Math.ceil(
        (100 * this.schedule.scheduled_items.length) /
          (this.schedule.scheduled_items.length + this.schedule.unscheduled_items.length),
      );
    },
    isSelectedEvenly() {
      return this.newConfig.venue_balance_method == "evenly";
    },
    constraintsSummary() {
      var str = "";
      var x = [];
      var c = this.schedule.config;
      if (c.max_games_day) {
        x.push(this.$t("max-c-max_games_day-this-itemtype-per-day", [c.max_games_day, this.itemType]));
      }
      if (c.max_games_week) {
        x.push(this.$t("max-c-max_games_week-this-itemtype-per-week", [c.max_games_week, this.itemType]));
      }
      if (c.next_game_offset) {
        x.push(this.$t("min-c-next_game_offset-nights-between-this-itemtype", [c.next_game_offset, this.itemType]));
      }
      if (c.min_minutes_rest) {
        x.push(this.$t("min-c-min_minutes_rest-minutes-between-this-itemtype", [c.min_minutes_rest, this.itemType]));
      }
      str = x[0] || "";
      x.forEach((z, i) => {
        if (i != 0) {
          str += " - " + z;
        }
      });
      return str;
    },
    itemType() {
      return this.schedule.is_practice ? this.$t("item_string.practices") : this.$t("item_string.games");
    },
    scheduleSummary2() {
      if (!this.schedule.config.input) {
        return "";
      }
      var str = "";
      var z = this.itemType;
      if (this.schedule.config.style == "rounds") {
        str += this.schedule.config.input + this.$t("rounds-per-team");
      } else if (this.schedule.config.style == "week") {
        str += this.schedule.config.input + " " + z + " " + this.$t("per-team-per-week");
      } else {
        str += this.schedule.config.input + " " + z + " " + this.$t("per-team-total");
      }
      // {{ slotsNeeded  ? parseInt(2 * slotsNeeded / division.teams.length) : '--' }} per team, {{ slotsNeeded }} total
      return str;
    },
    totalGamesLength() {
      if (this.schedule) {
        return (this.schedule.scheduled_items || []).length + (this.schedule.unscheduled_items || []).length;
      }
      return 0;
    },
    isMaster() {
      return !this.$route.params.schedule_id;
    },
    onSchedulePage() {
      var osp = window.location.href.includes("/schedule") || window.location.href.includes("/list");
      return osp;
    },
    scheduleSummary() {
      if (!this.schedule.config.schedule_start || !this.schedule.config.schedule_end) {
        return this.$t("imported-or-manual-schedule");
      }
      var s = this.$moment(this.schedule.config.schedule_start);
      var e = this.$moment(this.schedule.config.schedule_end);
      var str = s.format("LL") + " to " + e.format("LL");
      // str += + " (" + Math.ceil(e.diff(s, 'day') / 7) + " weeks)"
      str += " - " + Math.ceil(e.diff(s, "day") / 7).toString() + " weeks";
      return str;
    },
    tableItems() {
      console.log("STITEMS....");
      var items = [];
      var x =
        this.filterScheduleItems(
          this.schedule.scheduled_items,
          this.selectedTeams,
          this.selectedDivisions,
          this.selectedVenues,
        ) || [];
      x.forEach(item => [
        items.push({
          id: item.id,
          week: this.$moment(item.timeslot.start).isoWeek(),
          date: this.$moment(item.timeslot.start).format("MM-DD-YY"),
          time: this.$moment(item.timeslot.start).format("LT"),
          home: {
            name: item.game.home.name,
            id: item.game.home.id,
            score: item.game.home_score,
          },
          visitor: {
            name: item.game.visitor.name,
            id: item.game.visitor.id,
            score: item.game.visitor_score,
          },
          venue: {
            name: item.timeslot.venue.name,
            id: item.timeslot.venue.id,
          },
        }),
      ]);
      return items;
    },
  },
  methods: {
    dbPrint() {
      this.isPrinting = true;
      setTimeout(() => {
        this.$htmlToPaper("dbTable");
      }, 200);
      setTimeout(() => {
        this.isPrinting = false;
      }, 300);
    },
    getDbVenues(entries) {
      console.log("GBDV");
      console.log(entries);
      var x = [];
      for (let key in entries) {
        console.log(key, entries[key]);
        for (let mey in entries[key]) {
          if (!x.includes(mey)) {
            x.push(mey);
          }
        }
      }
      // this.dbVenues.forEach((v)=> {
      //   x.push(v)
      // })
      return x;
    },
    //dbd
    startDayByDay() {
      console.log("Start DBD");
      this.dbDialog = true;
      this.dbData = {};
      // this.schedule.scheduled_items.forEach((i)=> {
      //   console.log(i.timeslot.start)
      //   var x = i.timeslot.venue.name + " - " + i.timeslot.surface.name
      //   venues.push(x)
      //   this.dbData[x] = {}
      // })
      // venues.forEach((v)=> {
      //   this.schedule.scheduled_items.forEach((i)=> {
      //     var z = i.timeslot.venue.name + " - " + i.timeslot.surface.name
      //     if (z == v) {
      //       var d = new Date(i.timeslot.start).toLocaleDateString()
      //       var x = this.dbData[z][d] || {}
      //       x[new Date(i.timeslot.start).toLocaleTimeString()] = i.game.visitor.name + " @ " +i.game.home.name
      //       this.dbData[z][d] = x
      //     }
      //   })
      // })
      this.schedule.scheduled_items.forEach(i => {
        var d = new Date(i.timeslot.start).toLocaleDateString();
        this.dbData[d] = {};
        this.schedule.scheduled_items.forEach(i => {
          if (d == new Date(i.timeslot.start).toLocaleDateString()) {
            this.dbData[d][
              new Date(i.timeslot.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
            ] = {};
          }
        });
      });

      this.schedule.scheduled_items.forEach(i => {
        var d = new Date(i.timeslot.start).toLocaleDateString();
        if (d == new Date(i.timeslot.start).toLocaleDateString()) {
          this.dbData[d][
            new Date(i.timeslot.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
          ] = {};
        }
      });

      this.schedule.scheduled_items.forEach(i => {
        var d = new Date(i.timeslot.start).toLocaleDateString();
        var s = i.timeslot.venue.name + " - " + i.timeslot.surface.name;
        if (!this.dbVenues.includes(s)) {
          this.dbVenues.push(s);
        }
        var t = new Date(i.timeslot.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        this.dbData[d][t][s] = i.game.visitor.name + "@" + i.game.home.name;
      });
    },
    toggleLog() {
      if (!this.schedule.log_url) {
        alert(this.$t("log-not-available"));
        return;
      }
      window.open(this.schedule.log_url, "_blank");
      // console.log("Toggle Log: ", this.schedule.log_url)
      // var url = this.schedule.log_url;
      // var jsonFile = new XMLHttpRequest();
      // jsonFile.open("GET",url,true);
      // jsonFile.send();
      // jsonFile.onreadystatechange = function() {
      //     if (jsonFile.readyState== 4 && jsonFile.status == 200) {
      //         // document.getElementById("id-of-element").innerHTML = jsonFile.responseText;
      //         console.log("Response Text")
      //         console.log(jsonFile.responseText)
      //     }
      // }
    },
    cancelSchedule() {
      if (confirm(this.$t("are-you-sure-you-want-to-cancel-this-schedule"))) {
        this.isCanceling = true; // add this line to set the state to true

        api
          .cancelSchedule(this.schedule.id)
          .then(response => {
            this.makeToast("success", this.$t("schedule-cancelled"));
          })
          .catch(error => {
            this.makeToast("error", this.$t("failed-to-cancel-schedule"));
          })
          .finally(() => {
            this.isCanceling = false; // add this line to set the state to false
          });
      }
    },
    updateTotals() {
      api.updateScheduleTotals(this.schedule.id).then(response => {
        console.log("Updated ✅");
      });
    },
    toggleVenueBalanceDialog(open) {
      console.log("toggleVenueBalanceDialog ✅");
      this.venueBalanceEditDialog = open;
    },
    copyEmbedCode() {
      if (!this.currentUser.upgraded) {
        this.makeToast("info", this.$t("paid-feature-upgrade-account-to-embed-your-schedule"));
        return;
      }
      var str = "<iframe src='" + window.location.origin + "/embed/" + this.schedule.id + "'></iframe>";
      this.$copyText(str).then(() => {
        this.makeToast("success", this.$t("copied-embed-code-to-clipboard"));
      });
      this.updateTotals();
    },
    copyShareLink() {
      if (!this.currentUser.upgraded) {
        this.makeToast("info", this.$t("paid-feature-upgrade-account-to-share-a-link"));
        return;
      }
      this.api;
      var str = window.location.origin + "/embed/" + this.schedule.id;
      this.$copyText(str).then(() => {
        this.makeToast("success", this.$t("copied-share-link-to-clipboard"));
      });
      this.updateTotals();
    },
    viewCalendar() {
      this.goRouter(`/calendar?schedule_id=${this.schedule.id}`);
    },
    configUpdate(d) {
      console.log("ConfigUpdate", d);
      var c = this.newConfig;
      if (c.max_games_day > 0 && c.max_games_week > 0 && c.max_games_day > c.max_games_week) {
        console.log("DoMaxGamesDay");
        c.max_games_week = c.max_games_day;
      }
      if (c.max_games_week > 0 && c.max_games_day > c.max_games_week) {
        console.log("DoMaxGamesDay");
        c.max_games_day = c.max_games_week;
      }
      if (c.max_games_day == 1) {
        console.log("DoMinsRest");
        c.min_minutes_rest = 0;
      }
      if (c.max_games_day > 1) {
        console.log("DoMinsRest");
        c.next_game_offset = 0;
      }
    },
    getOpenSlots() {
      this.showingOpenSlots = true;
      console.log("⭐️⭐️⭐️ GetOpenTimeslots", this.schedule.open_timeslots.length);
      if (this.isMaster) {
        console.log("Master");
        api
          .getSeasonTimeslots(this.$store.getters.currentSeason.id)
          .then(response => {
            console.log("Master Timeslots response: ", response);
            this.schedule.open_timeslots = response.data;
            setTimeout(() => {
              var x = document.querySelector(".p-sortable-column");
              if (x && !x.classList.toString().includes("p-highlight")) {
                x.click();
              }
            }, 500);
          })
          .catch(e => {});
      } else {
        console.log("Schedule");
        api
          .getSeasonTimeslots(this.$store.getters.currentSeason.id)
          .then(response => {
            console.log("Timeslots response: ", response);
            this.schedule.open_timeslots = response.data;
            setTimeout(() => {
              var x = document.querySelector(".p-sortable-column");
              if (x && !x.classList.toString().includes("p-highlight")) {
                x.click();
              }
            }, 500);
          })
          .catch(e => {});
      }
    },
    updateItems(ids) {
      console.log("⭐️ UpdateItems", ids);
      api
        .refreshItems(this.currentSeason.id, ids)
        .then(response => {
          this.getSchedule();
          // console.log("🌕 Slots / Items: " + response.data.timeslots.length + " / " + response.data.items.length)
          // console.log(response.data)
          // var items = response.data.items
          // var found = false
          // items.forEach(item=> {
          //   this.schedule.scheduled_items.forEach((s,i)=> {
          //     if (s.id == item.id) {
          //       found = true
          //       console.log("✅ REPLACE GAME", this.schedule.scheduled_items[i], item)
          //       this.schedule.scheduled_items.splice(i,1, item)
          //     }
          //   })
          //   if (!found) {
          //     console.log("🔴 Could not find game")
          //     this.getSchedule()
          //     return
          //   }
          // })
          // var slots = response.data.timeslots
          // slots.forEach(slot=> {
          //   found = false
          //   console.log("Looking for slot ID " + slot.id + " starting at " + slot.start)
          //   this.schedule.open_timeslots.forEach((s,i)=> {
          //     if (s.id == slot.id) {
          //       // console.log("✅ REPLACE TIMESLOT", this.schedule.open_timeslots[i].start, slot.start)
          //       console.log("✅ Found replacement starting at " + s.start)
          //       this.schedule.open_timeslots.splice(i,1, slot)
          //       found = true
          //     }
          //   })
          //   if (!found) {
          //     console.log("🔴 Could not find slot")
          //     this.getSchedule()
          //     return
          //   }
          // })
          // this.schedule.balances = response.data;
          // console.log("Balances now...", this.schedule.balances);
          // this.loadingBalances = false;
          // this.$forceUpdate();
        })
        .catch(e => {
          // this.makeToast("error", "Could get balances");
          // console.log(e);
          // this.loadingBalances = false;
        });
    },
    rescheduleDelete(id) {
      var x = null;
      console.log("Reschedule delete", id);
      this.schedule.unscheduled_items.forEach((g, i) => {
        console.log("Check ", g);
        if (g.id == id) {
          this.schedule.unscheduled_items.splice(i, 1);
        }
      });
      this.schedule.unscheduled = this.schedule.unscheduled_items.length;
    },
    doGetBalance(showDialog = true) {
      if (this.isMaster) {
        this.getMasterBalance(showDialog);
      } else {
        this.getBalance(showDialog);
      }
    },
    getMasterBalance(showDialog = true) {
      if (!this.schedule) {
        return;
      }
      console.log("GetMasterBalance");
      this.loadingBalances = true;
      this.balanceDialog = showDialog;
      api
        .getMasterBalance(this.currentSeason.id)
        .then(response => {
          console.log("Got response", response);
          this.schedule.balances = response.data;
          console.log("Balances now...", this.schedule.balances);
          this.loadingBalances = false;
          this.$forceUpdate();
        })
        .catch(e => {
          this.makeToast("error", this.$t("could-not-load-balances"));
          console.log(e);
          this.loadingBalances = false;
          this.balanceDialog = false;
        });
    },
    getBalance(showDialog = true) {
      if (!this.schedule) {
        return;
      }
      console.log("GetBalance");
      this.loadingBalances = true;
      api
        .getBalance(this.schedule.id)
        .then(response => {
          console.log("Got response", response);
          this.schedule.balances = response.data;
          console.log("Balances now...", this.schedule.balances);
          this.loadingBalances = false;
          this.$forceUpdate();
          this.balanceDialog = showDialog;
        })
        .catch(e => {
          this.makeToast("error", this.$t("could-not-load-balances"));
          console.log(e);
          this.loadingBalances = false;
          this.balanceDialog = false;
        });
    },
    assign(scheduleitem) {
      this.getSchedule();
      // console.log("Assign", scheduleitem);
      // // Remove from unscheduled
      // var items = this.schedule.unscheduled_items;
      // items.forEach((item, i) => {
      //   if (scheduleitem.id == item.id) {
      //     console.log("✅ Remove unscheduled " + i);
      //     items.splice(i, 1);
      //   }
      // });
      // var tid = scheduleitem.timeslot.id
      // console.log("Look for timeslot id " + scheduleitem.timeslot.id)
      // this.schedule.open_timeslots.forEach((t, i)=> {
      //   if (t.id == tid) {
      //       console.log("✅ Remove Timeslot", t)
      //       this.schedule.open_timeslots.splice(i,1)
      //     }
      // })
      // this.schedule.unscheduled--;
      // this.schedule.scheduled_items.push(scheduleitem);
      // this.schedule.scheduled++;

      // Add to scheduled
    },
    gameUpdated(id, unscheduled) {
      console.log("✅ GAME UPDATED", id, unscheduled);
      this.removeItem(id, unscheduled);
    },
    removeItem(id, unscheduled) {
      console.log("RemoveItem", id, unscheduled);
      var items = this.schedule.scheduled_items;
      var x = null;
      items.forEach((item, i) => {
        if (id == item.id) {
          x = Object.assign({}, item);
          console.log("Found to remove at index " + i);
          items.splice(i, 1);
        }
      });
      if (x) {
        this.schedule.scheduled--;
        if (unscheduled) {
          x.timeslot = null;
          this.schedule.unscheduled_items.push(x);
          this.schedule.unscheduled++;
        }
      } else {
        console.log("Could not find item id " + id);
      }
    },
    gameCreated(item) {
      this.schedule.scheduled++;
      this.schedule.scheduled_items.push(item);
      console.log("Pusshed");
    },
    startDelete() {
      this.deleting = true;
      api
        .deleteSchedule({
          id: this.schedule.id,
          name: this.newName,
          se: this.deleteSe,
        })
        .then(response => {
          console.log("Response", response);
          console.log(this.deleteSe);
          if (this.deleteSe) {
            console.log("Go: " + response.data.activity.id);
            this.goRouter("/activity/" + response.data.activity.id);
            return;
          } else {
            this.makeToast("success", this.$t("deleted-schedule"));
          }
          this.refreshLeagueInformation("/schedules");
          this.isLoading = false;
        })
        .catch(e => {
          this.makeToast("error", this.$t("could-not-delete-schedule"));
          console.log(e);
          this.isLoading = false;
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    save() {
      console.log("Save: " + this.newName);
      api.updateSchedule({ id: this.schedule.id, name: this.newName }).then(response => {
        this.schedule.name = this.newName;
        this.makeToast("success", this.$t("updated-schedule"));
        this.editDialog = false;
      });
    },
    onToggle(value) {
      this.selectedColumns = this.columns.filter(col => value.includes(col));
    },
    getSchedule() {
      if (!this.onSchedulePage) {
        this.stopChecking();
        return;
      }
      console.log("⭐️ GetSchedule ⭐️");
      if (!this.currentSeason) {
        console.log("No Current Season...");
        this.refreshLeagueInformation(window.location.pathname);
        setTimeout(() => {
          this.getSchedule();
        }, 1000);
        return;
      }
      if (this.isMaster) {
        api
          .getMasterSchedule(this.currentSeason.id)
          .then(response => {
            console.log("✅ Master Schedule", response.data);
            this.schedule = response.data;
            if (this.showingOpenSlots || this.schedule.unscheduled_items.length) {
              this.getOpenSlots();
            }
            // if (!this.isProduction) {
            //   this.doGetBalance()
            // }
            this.isLoading = false;
            this.stopChecking();
          })
          .catch(e => {
            this.makeToast("error", this.$t("error-loading-schedule"));
            window.analytics.track(this.$t("schedule-load-fail"), {
              id: this.$route.params.schedule_id,
              error: e,
              master: true,
            });
            this.stopChecking();
          });
      } else {
        api
          .getSchedule(this.$route.params.schedule_id)
          .then(response => {
            console.log("Schedule:", response.data);
            this.schedule = response.data;
            this.newName = this.schedule.name;
            this.isLoading = false;
            if (this.showingOpenSlots || this.schedule.unscheduled_items.length) {
              this.getOpenSlots();
            }
            // if (!this.isProduction) {
            //   this.doGetBalance()
            // }
          })
          .catch(e => {
            console.log("COuldn't load schedule....", e);
            this.makeToast("error", this.$t("error-loading-schedule"));
            window.analytics.track(this.$t("schedule-load-fail"), {
              id: this.$route.params.schedule_id,
              error: e,
            });
            if (this.isProduction) {
              this.refreshLeagueInformation("/");
            } else {
              this.sayDev(this.$t("not-refreshing"));
            }
            this.stopChecking();
          });
      }
    },
    getStatus() {
      if (!this.onSchedulePage) {
        this.stopChecking();
        return;
      }
      if (this.isMaster) {
        console.log("No id, return");
        this.getSchedule();
        return;
      }
      api
        .getScheduleStatus(this.$route.params.schedule_id)
        .then(response => {
          console.log(response.data);
          if (response.data.scheduled > this.scheduleStats.bestCount) {
            this.scheduleStats.bestRotation = this.scheduleStats.attempt;
            this.scheduleStats.bestCount = response.data.scheduled;
          }
          this.schedule = response.data;
          if (response.data.finished) {
            this.stopChecking();
            this.progress = 100;
            this.getSchedule();
            return;
          } else if (this.checkInterval) {
            if (response.data.scheduled < this.scheduleStats.scheduled) {
              this.scheduleStats.attempt++;
              this.scheduleStats.bestCount = Math.max(response.data.scheduled, this.scheduleStats.bestCount);
              this.sayDev("Best count:" + this.scheduleStats.bestCount);
              if (response.data.scheduled > this.scheduleStats.bestCount) {
                this.scheduleStats.bestRotation = this.scheduleStats.attempt - 1;
              }
            }
            this.scheduleStats.scheduled = response.data.scheduled;
            this.scheduleStats.remaining = response.data.unscheduled;
            this.scheduleStats.total = this.scheduleStats.scheduled + this.scheduleStats.remaining;
            this.progress = Math.max(
              Math.floor((100 * response.data.scheduled) / (response.data.scheduled + response.data.unscheduled)),
              this.scheduleStats.percentage,
            );
            this.scheduleStats.percentage = this.progress;
          } else {
            console.log("Fresh, start checking...");
            this.startChecking();
            setTimeout(() => {
              this.isLoading = false;
            }, 1500);
          }
        })
        .catch(e => {
          console.log(e);
          this.stopChecking();
          this.makeToast("error", this.$t("error-loading-schedule"));
          window.analytics.track(this.$t("schedule-load-fail"), {
            id: this.$route.params.schedule_id,
            error: e,
          });
          if (this.isProduction) {
            this.refreshLeagueInformation("/");
          }
        });
    },
    stopChecking() {
      clearInterval(this.checkInterval);
    },
    startChecking() {
      this.progress = 0;
      if (this.schedule) {
        this.schedule.finished = false;
      }
      this.checkInterval = setInterval(() => {
        this.getStatus();
      }, 1500);
    },
    submitRetry() {
      console.log("Retry schedule...");
      (this.scheduleStats = {
        attempt: 1,
        scheduled: 0,
        remaining: 0,
        total: 0,
        percentage: 0,
        bestCount: 0,
        bestRotation: 1,
      }),
        // this.isRetry = true
        window.analytics.track(this.$t("retried-schedule"), {
          url: window.location.href,
          config: this.newConfig,
        });
      api.updateScheduleConfig(this.newConfig).then(response => {
        this.retryModal = false;
        console.log(response);
        this.startChecking();
      });
    },
    printTable(tableName) {
      console.log("PrintTable: " + tableName);
      this.isPrinting = true;
      setTimeout(() => {
        this.$htmlToPaper(tableName);
        setTimeout(() => {
          this.isPrinting = false;
        }, 500);
      }, 500);
    },
    printAllBalances() {
      this.isPrinting = true;
      this.isAll = true;
      setTimeout(() => {
        this.$htmlToPaper("allBalances");
        setTimeout(() => {
          this.isPrinting = false;
          this.isAll = false;
        }, 500);
      }, 500);
    },
    exportRest() {
      var data;
      var filename = "Rest-" + new Date().toLocaleString() + ".csv";
      var skinny = [];
      this.schedule.balances.week_names.forEach(i => {
        if (i != this.$t("GameTable.column.team")) {
          skinny.push({
            WEEK_START: i,
            REST_TREAMS: this.wrap((this.schedule.balances.cweek_balance[i] || []).join(", ")),
          });
        }
      });
      // this.tableItems.forEach((i) => {
      //   console.log(i.start, i.finish)
      //   skinny.push({
      //     DATE: this.dateToMDY(i.start),
      //     START_TIME: i.blocked || i.start == "HIDDEN" ? "HIDDEN" : !i.start ? "-" : this.$moment(i.start).format("LT"),
      //     END_TIME: i.blocked || i.finish == "HIDDEN" ? "HIDDEN" : !i.finish ? "-" : this.$moment(i.finish).format("LT"),
      //     VISITOR_TEAM: i.blocked || i.start == "HIDDEN" ? "HIDDEN" : i.visitor ? i.visitor.name : "",
      //     HOME_TEAM: i.blocked || i.start == "HIDDEN" ? "HIDDEN" : i.home ? i.home.name : "",
      //     VENUE: i.blocked || i.start == "HIDDEN" ? "HIDDEN" : i.venue ? i.venue.name : "",
      //     SURFACE: i.blocked || i.start == "HIDDEN" ? "HIDDEN" : i.surface ? i.surface.name : "",
      //     VISITOR_DIVISION: i.game && i.game.visitor.division ? i.game.visitor.division.name : "",
      //     HOME_DIVISION: i.game && i.game.home.division ? i.game.home.division.name : "",
      //     HOME_TEAM_CODE: i.game && i.game.home && i.game.home.longcode || "",
      //     VISITOR_TEAM_CODE: i.game && i.game.visitor && i.game.visitor.longcode || "",
      //     HOME_DIVISION_CODE: i.game && i.game.home && i.game.home.division.longcode || "",
      //     VISITOR_DIVISION_CODE: i.game && i.game.visitor && i.game.visitor.division.longcode || "",
      //     HOME_ID: i.game && i.game.home && i.game.home.id || "",
      //     VISITOR_ID: i.game && i.game.visitor && i.game.visitor.id || "",
      //     HOME_ABBREV: i.game && i.game.home && i.game.home.shortcode || "",
      //     VISITOR_ABBREV: i.game && i.game.visitor && i.game.visitor.shortcode || "",
      //     HOME_DIVISION_ID: i.game && i.game.home && i.game.home.division_id || "",
      //     VISITOR_DIVISION_ID: i.game && i.game.visitor && i.game.visitor.division_id || "",
      //     HOME_DIVISION_ABBREV: i.game && i.game.home && i.game.home.division.shortcode || "",
      //     VISITOR_DIVISION_ABBREV: i.game && i.game.visitor && i.game.visitor.division.shortcode || "",
      //     VENUE_ID: i.venue && i.venue.id || "",
      //     VENUE_ABBREV: i.venue && i.venue.shortcode || "",
      //     SURFACE_ID: i.surface && i.surface.id || "",
      //     HOME_SE_ID: i.game && i.game.home && i.game.home.se_id || "",
      //     VISITOR_SE_ID: i.game && i.game.visitor && i.game.visitor.se_id || "",
      //     HOME_SE_MAPPING_CODE: i.game && i.game.home && i.game.home.se_mapping_code || "",
      //     VISITOR_SE_MAPPING_CODE: i.game && i.game.visitor && i.game.visitor.se_mapping_code || "",
      //     HOME_DIVISION_SE_ID: i.game && i.game.home && i.game.home.division.se_id || "",
      //     VISITOR_DIVISION_SE_ID: i.game && i.game.home && i.game.visitor.division.se_id || "",
      //     HOME_DIVISION_SE_MAPPING_CODE: i.game && i.game.home && i.game.home.division.se_mapping_code || "",
      //     VISITOR_DIVISION_SE_MAPPING_CODE: i.game && i.game.visitor && i.game.visitor.division.se_mapping_code || "",
      //     SEASON_ID: this.currentSeason && this.currentSeason.id || "",
      //     LEAGUE_ID: this.currentLeague && this.currentLeague.id  || "",
      //     SE_SEASON_ID: this.currentSeason && this.currentSeason.se_id || "",
      //     SE_SUBSEASON_ID: this.currentSeason && this.currentSeason.se_subseason_id || "",
      //     UNIX_TIMESTAMP: new Date(i.start).getTime() / 1000,
      //   });
      // });
      var csv = this.convertArrayOfObjectsToCSV({
        data: skinny,
      });
      console.log("GOt CSV", csv);
      if (csv == null) return;

      if (!csv.match(/^data:text\/csv/i)) {
        csv = "data:text/csv;charset=utf-8," + csv;
      }
      data = encodeURI(csv);
      var link = document.createElement("a");
      document.body.appendChild(link);
      link.setAttribute("href", data);
      link.setAttribute("download", filename);
      link.click();
      document.body.removeChild(link);
      console.log("CLICKED!");
    },
    exportTable(tableName) {
      console.log("export table: " + tableName);
      switch (tableName) {
        case "venues":
          // code block
          this.$refs.venuesTable.exportCSV();
          break;
        case "games":
          this.$refs.gamesTable.exportCSV();
          // code block
          break;
        case "teams":
          this.$refs.teamsTable.exportCSV();
          // code block
          break;
        case "scores":
          this.$refs.scoresTable.exportCSV();
          // code block
          break;
        case "times":
          this.$refs.timesTable.exportCSV();
          break;
        case "weeks":
          this.$refs.weekTable.exportCSV();
          break;
        case "days":
          this.$refs.daysTable.exportCSV();
          break;
        default:
        // code block
      }
    },
  },
  watch: {
    retryModal() {
      var x = this.schedule.unscheduled_items;
      if (!x || !x.length) {
        this.newConfig.fresh = true;
      }
    },
    editDialog() {
      this.newName = this.schedule.name;
    },
    "$route.params.schedule_id": function(id) {
      this.isLoading = true;
      this.getSchedule();
      window.analytics.page();
    },
    filterChoice() {
      this.filters = {};
    },
    retryModal: function() {
      if (this.retryModal) {
        this.newConfig.id = this.schedule.config.id;
        this.newConfig.min_minutes_rest = this.schedule.config.min_minutes_rest || 0;
        this.newConfig.next_game_offset = this.schedule.config.next_game_offset || 0;
        this.newConfig.enforce_home = this.schedule.config.enforce_home;
        this.newConfig.max_games_day = this.schedule.config.max_games_day || 0;
        this.newConfig.max_games_week = this.schedule.config.max_games_week || 0;
        this.newConfig.fresh = this.schedule.unscheduled_items.length == 0;
        this.newConfig.use_coach_conflict_method = this.schedule.config.use_coach_conflict_method;
        this.newConfig.do_balance = this.schedule.config.do_balance;
        this.newConfig.use_weekend_method = this.schedule.config.use_weekend_method;
        this.newConfig.friday_as_weekend = this.schedule.config.friday_as_weekend;
        this.newConfig.balance_venues = this.schedule.config.balance_venues;
        this.newConfig.venue_balance_method = this.schedule.config.venue_balance_method;
        this.newConfig.venue_balance_tolerance = this.schedule.config.venue_balance_tolerance;
        console.log("NC", this.newConfig);
      }
    },
  },
  created() {
    this.isLoading = true;
    this.columns = [
      // { field: "id", header: "Item ID", sortable: true },
      // { field: "week", header: "Week #", sortable: true },
      { field: "date", header: this.$t("GameTable.column.date"), sortable: true },
      { field: "time", header: this.$t("GameTable.column.time"), sortable: true },
      { field: "home.name", header: this.$t("GameTable.column.home"), sortable: true },
      { field: "visitor.name", header: this.$t("GameTable.column.visitor"), sortable: true },
      { field: "home.score", header: this.$t("home-score"), sortable: true },
      { field: "visitor.score", header: this.$t("visitor-score"), sortable: true },
      { field: "venue.name", header: this.$t("GameTable.column.venue"), sortable: true },
    ];
    this.selectedColumns = this.columns;
  },
  mounted() {
    this.getStatus();
    console.log("🔥 Time: " + new Date());
    window.analytics.page();
  },
};
</script>

<style scoped lang="scss">
.p-datatable-sm {
  font-size: 12px;
}
</style>

<style>
.dbDiv {
  display: block;
  width: 95%;
  margin: 0 auto;
  margin-bottom: 50px;
}
table td[data-v-79380151]:not([align]),
table th[data-v-79380151]:not([align]) {
  width: auto;
}
.venueConfigurationContent {
  display: flex;
  justify-content: center;
  align-items: center;
}

#balanceExampleTable th,
#balanceExampleTable tr,
#balanceExampleTable td {
  border: 1px solid grey;
  padding: 5px;
  border-collapse: collapse;
  font-size: 1em;
}

#balanceExampleTable th {
  background-color: #d3d3d3;
}
</style>
